import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { isAuthed } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import Navbar from "@/components/Navbar";
import { ArrowUpRight, Trophy, Compass, MapPin, Award } from "lucide-react";

/**
 * Full-page index of every published treasure hunt — reachable from any
 * "See all N" link (Home, Guest Dashboard). Reuses the same immersive 4:5
 * card language as the home section so the transition feels seamless.
 */
export default function HuntsIndex() {
  const { lang } = useLang();
  const IT = lang === "it";
  const [hunts, setHunts] = useState([]);
  const [leader, setLeader] = useState(null);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/hunts")
      .then((r) => setHunts(r.data || []))
      .catch(() => setHunts([]))
      .finally(() => setLoading(false));
    api.get("/hunts/leaderboard/weekly").then((r) => setLeader(r.data?.leader || null)).catch(() => {});
    if (isAuthed()) {
      api.get("/hunts/leaderboard/weekly/me")
        .then((r) => setMe(r.data?.has_activity ? r.data : null))
        .catch(() => setMe(null));
    }
    document.title = IT
      ? "Mappa del Tesoro — Cinque città italiane da vivere a piedi | HospitalityHub"
      : "Treasure Hunts — Five Italian cities to walk into | HospitalityHub";
  }, [IT]);

  return (
    <div className="min-h-screen bg-background">
      <Navbar/>
      <section className="max-w-6xl mx-auto px-6 py-20" data-testid="hunts-index-page">
        <div className="text-center mb-12">
          <div className="text-[11px] uppercase tracking-[0.35em] text-primary mb-3 font-semibold flex items-center justify-center gap-2">
            <Compass className="w-3 h-3"/>{IT ? "Mappa del Tesoro" : "Treasure Hunts"}
          </div>
          <h1 className="font-display font-normal italic leading-[0.95] mb-4" style={{ fontSize: "clamp(2.5rem, 6vw, 4.5rem)" }}>
            {IT ? "Racconti che si vivono a piedi" : "Stories you walk into"}
          </h1>
          <p className="text-muted-foreground italic mt-4 max-w-2xl mx-auto text-lg">
            {IT
              ? "Sette segreti + sette tavole d'autore in ogni città. Percorsi pensati per chi vuole andare oltre le cartoline."
              : "Seven secrets + seven signature tables in every city. Walks for travellers who want more than postcards."}
          </p>
        </div>

        {(me || leader) && (
          <div className="max-w-2xl mx-auto mb-10 flex flex-col sm:flex-row items-center justify-center gap-2 text-sm">
            {leader && (
              <div className="flex items-center gap-2.5 bg-primary/5 border border-primary/30 rounded-full px-5 py-2.5" data-testid="hunts-index-leader">
                <Trophy className="w-4 h-4 text-primary shrink-0"/>
                <span>
                  <b className="text-primary">{leader.city}</b>{" "}
                  {IT
                    ? <>è in testa questa settimana con <b>{leader.count}</b> completamenti</>
                    : <>leads this week with <b>{leader.count}</b> completions</>}
                </span>
              </div>
            )}
            {me && (
              <div className="flex items-center gap-2.5 bg-sage/10 border border-sage/40 rounded-full px-5 py-2.5" data-testid="hunts-index-me">
                <span className="w-2 h-2 rounded-full bg-sage animate-pulse shrink-0"/>
                <span>
                  {IT
                    ? <>Sei il <b className="text-sage">#{me.rank}</b> su <b>{me.total}</b> questa settimana a <b>{me.city}</b></>
                    : <>You're <b className="text-sage">#{me.rank}</b> of <b>{me.total}</b> this week in <b>{me.city}</b></>}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Wall of Explorers teaser — links to the public social-proof gallery */}
        <div className="max-w-3xl mx-auto mb-14 text-center">
          <Link
            to="/muro-esploratori"
            className="inline-flex items-center gap-2 text-sm px-5 py-2.5 rounded-full border border-primary/30 text-primary hover:bg-primary hover:text-primary-foreground transition-all"
            data-testid="hunts-index-wall-link"
          >
            <Award className="w-4 h-4"/>
            {IT ? "Guarda chi ha completato — Muro degli Esploratori" : "See who completed — Wall of Explorers"}
            <ArrowUpRight className="w-3.5 h-3.5"/>
          </Link>
        </div>

        {loading ? (
          <div className="text-center text-muted-foreground italic py-16">{IT ? "Caricamento…" : "Loading…"}</div>
        ) : hunts.length === 0 ? (
          <div className="text-center text-muted-foreground italic py-16 border border-dashed border-border rounded-lg">
            {IT ? "Nessuna caccia pubblicata al momento." : "No hunts published yet."}
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {hunts.map((h) => (
              <Link
                key={h.id}
                to={`/caccia-al-tesoro/${h.id}`}
                className="group block relative aspect-[4/5] overflow-hidden rounded-sm shadow-lg lift"
                data-testid={`hunts-index-card-${h.id}`}
              >
                <img
                  src={h.hero_image}
                  alt={h.title || ""}
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  loading="lazy"
                />
                <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(35,31,27,0.15) 30%, rgba(35,31,27,0.9) 100%)" }}/>
                <div className="absolute inset-x-0 bottom-0 p-6 text-white">
                  <div className="text-[10px] uppercase tracking-[0.35em] opacity-80 mb-2 flex items-center gap-1.5">
                    <MapPin className="w-3 h-3"/>{h.city}
                  </div>
                  <div className="font-display italic text-3xl leading-tight">{h.title}</div>
                  {h.subtitle && (
                    <div className="text-sm opacity-85 italic mt-2 line-clamp-2">{h.subtitle}</div>
                  )}
                  {h.completed_this_month > 0 && (
                    <div className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-medium bg-white/15 backdrop-blur-sm border border-white/25 rounded-full px-2.5 py-1" data-testid={`hunts-index-count-${h.id}`}>
                      <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse"/>
                      {IT
                        ? `${h.completed_this_month} viaggiatori questo mese`
                        : `${h.completed_this_month} travellers this month`}
                    </div>
                  )}
                  <div className="text-xs mt-4 opacity-70 group-hover:opacity-100 group-hover:translate-x-1 transition-all inline-flex items-center gap-1">
                    {IT ? "Comincia il viaggio" : "Begin the journey"} <ArrowUpRight className="w-3 h-3"/>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
